import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ChatUI } from "@/components/ChatUI";
import { ChatWelcomeModal } from "@/components/ChatWelcomeModal";
import { STORAGE_EVENTS } from "@/constants/storage";

export function Home() {
  const { conversationId } = useParams<{ conversationId?: string }>();
  const navigate = useNavigate();

  // "new" is a sentinel path meaning "start fresh — ignore localStorage".
  const isNew = conversationId === "new";
  const initId = isNew ? null : conversationId;

  // mountKey drives ChatUI remounts. We frozen-update it on real URL
  // navigations between conversations, but skip the update for the
  // /chat → /chat/:id auto-redirect that fires once the BE assigns an
  // ID to a fresh conversation. Without that skip, the URL change would
  // remount ChatUI mid-stream and abort the reply.
  const [mountKey, setMountKey] = useState(() => conversationId ?? "default");
  const skipNextKeyUpdate = useRef(false);

  useEffect(() => {
    function onCreated(e: Event) {
      const id = (e as CustomEvent<{ id?: string }>).detail?.id;
      if (!id) return;
      // Only auto-navigate when the URL isn't already pinning a specific
      // conversation — i.e. /chat (root) or /chat/new (sentinel).
      if (conversationId === undefined || conversationId === "new") {
        skipNextKeyUpdate.current = true;
        navigate(`/chat/${id}`, { replace: true });
      }
    }
    window.addEventListener(STORAGE_EVENTS.conversationCreated, onCreated);
    return () => window.removeEventListener(STORAGE_EVENTS.conversationCreated, onCreated);
  }, [conversationId, navigate]);

  useEffect(() => {
    if (skipNextKeyUpdate.current) {
      skipNextKeyUpdate.current = false;
      return;
    }
    setMountKey(conversationId ?? "default");
  }, [conversationId]);

  return (
    <>
      <ChatUI key={mountKey} conversationId={initId} />
      {/* First-visit welcome. Self-gated via localStorage so it's a
          one-shot regardless of which /chat path the user lands on. */}
      <ChatWelcomeModal />
    </>
  );
}
